/**
 * Grid-based spatial index + vertex bitset.
 *
 * `SpatialGrid` lives in Rust (`../wasm/spatialIndex.ts`). `VertexBitset` is
 * a pure-JS bit-twiddling helper — no arithmetic to accelerate, stays here.
 */

export { SpatialGrid } from "../wasm/spatialIndex.ts";

// ---------------------------------------------------------------------------
// Reusable vertex bitset — O(1) membership test for vertex index dedup.
// Backed by a Uint32Array; clears only the bits that were set.
// ---------------------------------------------------------------------------

export class VertexBitset {
  private bits: Uint32Array;
  /** Indices of words that have been dirtied — used for O(touched) clear. */
  private dirtyWords: number[] = [];

  constructor(vertexCount: number) {
    this.bits = new Uint32Array((vertexCount + 31) >>> 5);
  }

  /** Ensure capacity for at least `vertexCount` vertices. */
  ensure(vertexCount: number): void {
    const needed = (vertexCount + 31) >>> 5;
    if (this.bits.length < needed) {
      this.bits = new Uint32Array(needed);
      this.dirtyWords.length = 0;
    }
  }

  /** Set bit for vertex index. Returns true if it was already set. */
  setAndTest(vi: number): boolean {
    const word = vi >>> 5;
    const mask = 1 << (vi & 31);
    if (this.bits[word] & mask) return true;
    if (this.bits[word] === 0) this.dirtyWords.push(word);
    this.bits[word] |= mask;
    return false;
  }

  /** Clear only the bits that were previously set — O(touched). */
  clear(): void {
    const { bits, dirtyWords } = this;
    for (const w of dirtyWords) bits[w] = 0;
    dirtyWords.length = 0;
  }
}
